import { ISemanticEvent, SemanticContext } from './semanticEvents';
import { Integration } from './integration';
import { EventBuffer } from './eventBuffer';

enum Flags {
  /**
   * Флаг показывает, что интеграция принимает события в очередь
   */
  Queue = 0b1,
  /**
   * флаг показывающий, что интеграция готова обрабатывать события
   */
  Ready = 0b10,
}

export abstract class AsyncIntegration implements Integration {
  private _state: number = Flags.Queue;
  /**
   * Очередь событий
   * @private
   */
  private _queue: EventBuffer<[ISemanticEvent, SemanticContext]>;

  constructor(
    readonly displayName: string,
    bufferSize = 50,
  ) {
    this._queue = new EventBuffer<[ISemanticEvent, SemanticContext]>(bufferSize);
  }

  trackEvent<T extends ISemanticEvent = ISemanticEvent>(event: T, ctx: SemanticContext) {
    if (!this.filterEvent(event)) {
      return;
    }
    if (this.ready) {
      this._trackEvent(event, ctx);
    } else if (this.queue_allow) {
      this.enqueueEvent(event, ctx);
    }
  }

  protected abstract _trackEvent(event: ISemanticEvent, ctx: SemanticContext): void;

  /**
   * Выполняет подготовку интеграции к обработке событий
   * @protected
   */
  protected abstract init(ctx?: SemanticContext): Promise<void>;

  get ready() {
    return Boolean(this._state & Flags.Ready);
  }

  get queue_allow() {
    return Boolean(this._state & Flags.Queue);
  }

  /**
   * Фильтр событий
   * Событие будет обработано, только если функция вернет true
   * Позволяет не нагружать очередь событиями, которые не обрабатываются
   * данной интеграцией
   * @protected
   */
  protected filterEvent(_e: ISemanticEvent): boolean {
    return true;
  }

  /**
   * Ставит событие в очередь
   * @protected
   */
  protected enqueueEvent(event: ISemanticEvent, ctx: SemanticContext): void {
    this.queue_allow && this._queue.push([event, ctx]);
  }

  /**
   * Обрабатывает очередь событий
   * @protected
   */
  protected flushEventQueue() {
    let record = this._queue.shift();
    while (record) {
      this.trackEvent(record[0], record[1]);
      record = this._queue.shift();
    }
  }

  /**
   * вызывается после успешной инициализации
   * @protected
   */
  protected onReady(): void {
    this._state = this._state | Flags.Ready;
    this.flushEventQueue();
  }

  protected onInitFail(): void {
    this.stopQueue();
    this.resetQueue();
  }

  /**
   * Сбрасывает очередь событий
   * @protected
   */
  protected resetQueue() {
    this._queue.reset();
  }

  /**
   * Останавливает прием событий в очередь
   * @protected
   */
  protected stopQueue() {
    this._state = this._state ^ Flags.Queue;
  }
}
